import { TrackingSystemParameters } from '@poseidon/tracklib/lib/tracklib.systems';
import Penpal, { IConnectionObject } from 'penpal';
import { Dimensions } from '../../client/interfaces';
import { PermissionClientMessaging } from '../../client/tools/interfaces';
import * as Logger from '../../common/tools/logger';
import { MonitoringCounterNames, MonitoringEventNames } from '../../common/tools/monitoring-interfaces';
import { monitoringService } from '../../common/tools/monitoring.service';
import { allSettled } from '../../common/tools/promise';
import { sanitizeValue } from '../../common/tools/sanitizer';
import { setTag, setUser } from '../../common/tools/sentry';
import * as tracker from '../../common/tools/tracker';
import { getTimeout, isInternational } from '../../common/tools/util';
import {
  CONFIG_CLIENT_SOFT_NAME,
  CONFIG_CORE_PERM_CONSENT_STRINGS,
  CONFIG_CORE_PERM_TGP,
  CONFIG_CORE_PPS_ENDPOINT_GUID,
  CONFIG_CORE_PPS_ENDPOINT_TPID,
  CONFIG_CORE_PPS_ENDPOINT_UAS,
  CONFIG_CORE_PPS_URL,
  CONFIG_CORE_SOFT_NAME,
  CONFIG_LAYER_SOFT_NAME
} from '../../config';
import {
  AditionClientConfig,
  AditionNextData,
  IdentityType,
  LayerParameters,
  PermissionData,
  PermissionModel
} from '../../interfaces';
import { PermissionLayerOptions, TcData } from '../../layer/interfaces';
import { PermissionCore } from '../interfaces';
import { AditionService } from './adition.service';
import {
  getPermissionsWithoutTimestamps,
  mergePermissionModels,
  syncConsentStrings,
  syncTgp,
  updatePermissionModel
} from './permission-model.util';
import { PpsConfig, PpsService } from './pps.service';

export type PermissionServices = {
  [id in IdentityType]?: PpsService;
};

const endpointForIdentity: { [id in IdentityType]: string } = {
  UAS_ACCOUNT_ID: CONFIG_CORE_PPS_ENDPOINT_UAS,
  NETID_TPID: CONFIG_CORE_PPS_ENDPOINT_TPID,
  MAM_ACCOUNT_GUID: CONFIG_CORE_PPS_ENDPOINT_GUID,
};

/**
 * @class
 * @name CommunicationHandlerService
 * @description Function to handle the communication between the components (Permission Layer, Core and Publisher Page).
 */
export class CommunicationHandlerService {

  /**
   * @var {string} moduleName Reflects the modules name.
   */
  private moduleName = `CommunicationHandlerService`;

  private aditionService: AditionService = new AditionService();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parentConnection: IConnectionObject<any>;

  /**
   * Permission Client (parent) API exposed through Penpal
   */
  private permissionClient: PermissionClientMessaging;

  private settings: PpsConfig;

  private clientConfig: AditionClientConfig;

  private permissionServices: PermissionServices = {};

  /**
   * Prepares permission service instances for each of the configured identity types
   * Due to portal restrictions in `initLayer`, this function gets executed only for the following portals:
   * - webde
   * - gmx
   * - gmxat
   * - gmxch
   */
  private initPermissionServices(): PermissionServices {
    const services: PermissionServices = {};
    const portal: PpsConfig['portal'] = this.settings.portal;

    if (!CONFIG_CORE_PPS_URL[portal]) {
      throw new Error(`CommunicationHandlerService: No PPS endpoint for portal '${this.settings.portal}' available!`);
    }

    Object.keys(endpointForIdentity).forEach((identity) => {
      const endpoint = endpointForIdentity[identity as IdentityType];
      services[identity as IdentityType] = new PpsService(`${CONFIG_CORE_PPS_URL[portal]}/${endpoint}`, this.settings);
    });

    Logger.debug(this.moduleName, 'initPermissionServices', `initialized for identity types:`, Object.keys(services));

    return services;
  }

  private async initLayer(settings: PpsConfig): Promise<void> {
    this.settings = settings;

    if (settings && typeof settings.accessToken === 'string') {
      try {
        this.permissionServices = this.initPermissionServices();
        await this.syncModels();
        // this.permissionModel is set after sync since we need to use the identity from the CATS response to retrieve
        // the user's model from local storage
      } catch (error) {
        Logger.error(this.moduleName, 'initLayer', error);
      }
    } else {
      // no access token -> no PPS connection -> no NETID_TPID from CATS -> use the identity provided by the publisher
      const identity = await this.getIdentity();
      this.permissionModel = await this.permissionClient.getModel(identity);
    }

    try {
      const params = await this.permissionClient.getParameters();
      const section = this.clientConfig && typeof this.clientConfig.section === 'string' ?
        this.clientConfig.section.split('/')[0] : null;
      monitoringService.setMetaData({
        brand: this.settings.portal,
        section
      });

      [
        CONFIG_CORE_SOFT_NAME,
        CONFIG_LAYER_SOFT_NAME,
        CONFIG_CLIENT_SOFT_NAME
      ].forEach((softwareName) => {
        tracker.initTracker({
          softwareName,
          brand: this.settings.portal,
          requestedPermission: params.forcePermissionLayer ? params.forcePermissionLayer : params.permissionGroup,
          touchpointName: this.settings.touchpointName,
          touchpointReference: this.settings.touchpointReference,
          tpid: this.permissionModel ? this.permissionModel.identity /* istanbul ignore next else */ : undefined
        });
      });

      const clientTrackingData = params.consentParameters &&
      params.consentParameters.trackingData ? params.consentParameters.trackingData : {};

      tracker.track(CONFIG_CORE_SOFT_NAME,{
        ...clientTrackingData,
        section: 'permission.request.start',
        sett_sample: 100
      });
    } catch (ex) {
      Logger.error(this.moduleName, 'initLayer', 'could not initialize tracking', ex);
    }

    await this.aditionService.init(this.permissionModel, this);
  }

  private parentApi: PermissionCore = {
    initializePermissionLayer: async (settings) => {
      Logger.info(this.moduleName, `initializePermissionLayer`, 'parent called initializePermissionLayer');

      this.setSentryTags({
        xUIApp: settings.xUIApp,
        touchpointName: settings.touchpointName,
        touchpointReference: settings.touchpointReference
      });

      if (this.permissionClient) {
        await this.initLayer(settings);
      } else {
        // Workaround for SWAT-2135
        const handle = window.setInterval(async () => {
          if (this.permissionClient) {
            window.clearInterval(handle);
            await this.initLayer(settings);
          }
        }, 100);
      }

    },

    layerDisplayed: async () => {
      await this.aditionService.layerDisplayed({
        touchpointName: this.settings.touchpointName
      });
    },

    trackBrain: async (params: TrackingSystemParameters) => {
      tracker.track(CONFIG_CLIENT_SOFT_NAME, params);
    }
  };

  /**
   * The currently active set of permissions. This will be updated as the user grants/revokes permissions
   */
  public permissionModel: PermissionModel = {
    identity: null,
    tcfVersion: 0,
    tcString: '',
    tcStatus: '',
    data: []
  };

  /**
   * @public
   * @async
   * @function initialize
   * @description Function to initialize a post message listener to handle commands.
   * @return {Promise<void>}
   */
  public async initialize(): Promise<void> {

    this.parentConnection = Penpal.connectToParent({
      // Methods child is exposing to parent
      methods: this.parentApi,
      timeout: getTimeout(),
    });
    this.permissionClient = await this.parentConnection.promise as PermissionClientMessaging;
    Logger.debug(this.moduleName, `initialize`, `Module initialized.`);
    this.clientConfig = await this.getAditionClientConfig();
    this.setSentryTags({
      ...this.clientConfig
    });
  }

  /**
   * @public
   * @function openPermissionLayer
   * @description Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   * @param {PermissionLayerOptions} options
   * @return {void}
   */
  public async openPermissionLayer(options?: PermissionLayerOptions): Promise<void> {
    return this.permissionClient.openPermissionLayer(options);
  }

  /**
   * @public
   * @function closePermissionLayer
   * @description Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   * @param success indicates that closePermissionLayer was called early since a previous operation failed
   * @param error error message to be passed to the onComplete handler
   * @return {void}
   */
  public async closePermissionLayer(success = true, error?: string): Promise<void> {
    if (success) {
      try {
        // Set the euconsent-v2 cookie before using the TCF API (within `syncTgp` and `syncConsentStrings`) to always use the latest tcString
        // missing this.settings means Permission Core was never initialized
        if (this.settings && Object.prototype.hasOwnProperty.call(this.permissionModel, 'tcString') && this.permissionModel.tcString) {

          await syncTgp(this.permissionModel);
          await syncConsentStrings(this.permissionModel, isInternational(this.settings.portal));

          const newPermissions = this.permissionModel.data
            .filter((perm) => {
              return !perm.timestamp && (
                perm.usecase.toUpperCase() === CONFIG_CORE_PERM_TGP ||
                perm.usecase.toUpperCase() === CONFIG_CORE_PERM_CONSENT_STRINGS
              );
            });

          if (newPermissions.length > 0) {
            Logger.info(this.moduleName, 'closePermissionLayer', `TCF sync required`, JSON.stringify(newPermissions, null, 2));
            monitoringService.pushCounter({
              name: MonitoringCounterNames.tcf_sync,
              event: MonitoringEventNames.started
            });

            if (this.settings && typeof this.settings.accessToken === 'string') {
              // store any permissions modified by the PMC sync logic
              await this.storePermissions(newPermissions);
              // read after write to make sure we are in sync with PMC
              await this.syncModels();
            }
            await this.permissionClient.setPermissions(this.permissionModel);
          } else {
            /* istanbul ignore next */
            Logger.debug(this.moduleName, 'closePermissionLayer', `TCF sync not required`);
          }
        } else {
          Logger.warn(this.moduleName, `closePermissionLayer`, `Permission Model does not contain a TCString, not able to execute syncTgp and synConsentStrings!`);
        }
      } catch (reason) {
        success = false;
        error = reason.toString();
        Logger.error(reason);

        monitoringService.pushCounter({
          name: MonitoringCounterNames.tcf_sync,
          event: MonitoringEventNames.failed
        });
      }
    }

    monitoringService.flush();

    if (this.permissionClient) {
      this.permissionClient.closePermissionLayer(success, error);
    }

    this.destroy();
  }

  public trackBrain(params: TrackingSystemParameters): void {
    tracker.track(CONFIG_LAYER_SOFT_NAME, params);
  }

  /**
   * Fetches remote permissions from all configured services
    */
  public async retrievePermissions(): Promise<PermissionData[][]> {
    const promises = Object.keys(this.permissionServices).map((identityType) => {
      Logger.info(this.moduleName, 'retrievePermissions', `Retrieving permissions for identity ${identityType} from PPS`);

      monitoringService.pushCounter({
        name: MonitoringCounterNames.pps_get_request,
        event: MonitoringEventNames.sent,
        identityType: identityType as IdentityType
      });

      return this.permissionServices[identityType as IdentityType].readPermissions();
    });

    const results = await allSettled(promises);

    results.filter((result) => result.status === 'rejected')
      .forEach((result) => {

        if (Object.prototype.hasOwnProperty.call(result, 'reason') && JSON.stringify(result.reason).toLowerCase().indexOf('timed out') > -1) {
          Logger.warn(this.moduleName, 'retrievePermissions', `Error while fetching remote model:`, result.reason);
        } else {
          Logger.error(this.moduleName, 'retrievePermissions', `Error while fetching remote model:`, result.reason);
        }

        monitoringService.pushCounter({
          name: MonitoringCounterNames.pps_get_request,
          event: MonitoringEventNames.failed,
          // TODO identityType
        });

        tracker.track(CONFIG_CORE_SOFT_NAME, {
          section: 'permission.pps.access.read',
          resu_pps: '44000001',
          sett_sample: 100
        });
      });

    return results.filter((result) => result.status === 'resolved')
      .map((result) => {
        /* istanbul ignore next else */
        if (result.value) {
          tracker.track(CONFIG_CORE_SOFT_NAME, {
            section: 'permission.pps.access.read',
            sett_sample: 100
          });
        }

        // ignore permissions with missing usecase names. This has been fixed in PPS but can still occur for older permission data
        return result.value.filter((remotePerm) => !!remotePerm.usecase);
      });
  }

  /**
   * Store permission data using the appropriate endpoint for each identity type
   * @param permissionData
   */
  public async storePermissions(permissionData: PermissionData[]): Promise<void> {
    if (Object.keys(this.permissionServices).length > 0) {
      const missingIdentities = permissionData.filter((data) => !data.identityType);
      if (missingIdentities.length > 0) {
        Logger.error(this.moduleName, 'storePermissions', `missing identityType in permission!`, missingIdentities);
      }
    }

    const promises = Object.keys(this.permissionServices).map((identityType) => {
      const dataByType = permissionData.filter((permission) => {
        return permission.identityType ? permission.identityType.toUpperCase() === identityType : false;
      });
      if (dataByType.length > 0) {
        Logger.info(this.moduleName, 'storePermissions', `Storing ${dataByType.length} updated permissions for identity ${identityType} in PPS`, JSON.stringify(dataByType,null, 2));

        dataByType.forEach((permission) => {
          monitoringService.pushCounter({
            name: MonitoringCounterNames.pps_post_request,
            event: MonitoringEventNames.sent,
            identityType: permission.identityType,
            usecase: permission.usecase,
            status: permission.status
          });
        });

        return this.permissionServices[identityType as IdentityType].writePermissions(dataByType);
      }
      return null;
    })
      .filter((promise) => promise !== null);

    return allSettled(promises)
      .then((results) => {
        results.filter((result) => result.status === 'resolved')
          .forEach(() => tracker.track(CONFIG_CORE_SOFT_NAME, {section: 'permission.pps.access.write'}));
        results.filter((result) => result.status === 'rejected')
          .forEach((result) => {
            tracker.track(CONFIG_CORE_SOFT_NAME, {section: 'permission.pps.access.write', resu_pps: '44000008'});
            Logger.error(`${this.moduleName} storePermissions: Error while writing permissions: ${result.reason}`);
            monitoringService.pushCounter({
              name: MonitoringCounterNames.pps_post_request,
              event: MonitoringEventNames.failed,
              // TODO identityType, usecase, status
            });
          });
      });
  }

  /**
   * Updates the model with the given permission (attributes will be merged)
   * @param permissionData
   */
  public async updatePermissions(permissionData: PermissionData[]): Promise<void> {
    Logger.info(this.moduleName, 'updatePermissions', 'Received updated permissions from layer', JSON.stringify(permissionData, null, 2));
    await this._updatePermissions(permissionData, true);
  }

  /**
   * Updates the model with the given permission (attributes will not be merged)
   * @param permissionData
   */
  public async setPermissions(permissionData: PermissionData[]): Promise<void> {
    Logger.info(this.moduleName, 'updatePermissions', 'Received updated permissions from layer', JSON.stringify(permissionData, null, 2));
    await this._updatePermissions(permissionData, false);
  }

  /**
   * @function updatePermissions
   * @description Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   * @param {PermissionData[]} permissionData
   * @param merge merge attributes if a permission with the same usecase exists in the model
   * @return {void}
   */
  protected async _updatePermissions(permissionData: PermissionData[], merge = true): Promise<void> {
    permissionData.forEach((permission) => {
      /* istanbul ignore next else */
      if (typeof permission.usecase === 'string' && typeof permission.status === 'string') {
        // make sure usecase name and status always use uppercase
        permission.usecase = permission.usecase.toUpperCase();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        permission.status = (permission as any).status.toUpperCase();
        this.permissionModel = updatePermissionModel(this.permissionModel, permission, merge);
      } else {
        Logger.error(this.moduleName, '_updatePermissions', 'called with invalid permission data:', JSON.stringify(permission, null, 2));
      }
    });

    try {
      await this.permissionClient.setPermissions(this.permissionModel);
    } catch (e) {
      Logger.error(this.moduleName, '_updatePermissions', 'exception in PermissionClient.updatePermissions', e);
    }

    try {
      await this.storePermissions(permissionData);
    } catch (e) {
      Logger.error(this.moduleName, '_updatePermissions', 'exception in storePermissions', e);
    }
  }

  /**
   * Update the TC string in the model and propagate the model update to the client.
   * @param tcData
   */
  public async setTCData(tcData: TcData): Promise<void> {
    Logger.info(this.moduleName, 'setTCData', 'Received updated tcData from layer', JSON.stringify(tcData, null, 2));
    try {
      this.permissionModel.tcString = tcData.tcString;
      this.permissionModel.tcfVersion = tcData.tcfVersion;
      this.permissionModel.tcStatus = tcData.tcStatus;
      await this.permissionClient.setPermissions(this.permissionModel);

      monitoringService.pushCounter({
        name: MonitoringCounterNames.tcf_data_update,
        event: MonitoringEventNames.successful,
        tcStatus: tcData.tcStatus
      });

    } catch (e) {
      Logger.error(this.moduleName, 'setTCData', e);

      monitoringService.pushCounter({
        name: MonitoringCounterNames.tcf_data_update,
        event: MonitoringEventNames.failed,
        tcStatus: tcData.tcStatus
      });
    }
  }

  /**
   * @public
   * @function updatePermissionLayer
   * @description Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   * @param {AditionNextData} nextData Data to be passed for a single next call
   * @return {void}
   */
  public async updatePermissionLayer(nextData?: AditionNextData): Promise<void> {
    await this.aditionService.init(this.permissionModel, this, nextData);
  }

  /**
   * @public
   * @function updatePermissionLayer
   * @description Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   * @param {Dimensions} dimensions
   * @return {void}
   */
  public resizePermissionLayer(dimensions: Dimensions): void {
    this.permissionClient.resizePermissionLayer(dimensions);
  }

  /**
   * @public
   * @async
   * @function getAditionClientConfig
   * @description Function to get the required ADITION config, set by the Permission Client.
   * @return {Promise<AditionClientConfig>}
   */
  public async getAditionClientConfig(): Promise<AditionClientConfig> {
    return this.permissionClient.getAditionClientConfig();
  }

  /**
   * @public
   * @async
   * @description Function to get custom consent parameters from the publisher page.
   * @return {Promise<LayerParameters>}
   */
  public async getLayerParameters(): Promise<LayerParameters> {
    const params = await this.permissionClient.getParameters();
    if (params.consentParameters) {
      params.consentParameters = sanitizeValue(params.consentParameters);
    }
    return params;
  }

  /**
   * @public
   * @async
   * @function getIdentity
   * @description Get users identity from Introspection API or client
   * @return {Promise<string>}
   */
  public async getIdentity(): Promise<string> {
    if (this.permissionServices.NETID_TPID) {
      const params = this.permissionServices.NETID_TPID.getTargetPathParameters('NETID_TPID');
      if (params && params.identity) {
        Logger.debug(this.moduleName, 'getIdentity', `using NETID_TPID from CATS`);
        // add TPID to Sentry events
        setUser({
          id: params.identity
        });
        return params.identity;
      }
    }
    const clientIdentity = await this.permissionClient.getIdentity();
    Logger.debug(this.moduleName, 'getIdentity', `using NETID_TPID from client`);
    return clientIdentity;
  }

  /**
   * Synchronize the local model with remote data from the permission service(s)
   */
  protected async syncModels(): Promise<void> {
    Logger.debug(this.moduleName, 'syncModels', 'Fetching remote permissions...');
    const remotePermissionData = await this.retrievePermissions();

    if (!remotePermissionData || remotePermissionData.length === 0) {
      tracker.track(CONFIG_CORE_SOFT_NAME, {section: 'permission.sync.localremote', resu_sync: '44000003'});
      return;
    }

    Logger.info(this.moduleName, 'syncModels', `Retrieved remote permissions:`, JSON.stringify(remotePermissionData, null, 2));

    // get the model for the user's identity
    const identity = await this.getIdentity();
    this.permissionModel = await this.permissionClient.getModel(identity);

    remotePermissionData.push(this.permissionModel.data);
    this.permissionModel.data = remotePermissionData.reduce((acc, val) => {
      // create a temporary model so we can use mergePermissionModels
      const tempModel: PermissionModel = {
        identity: null,
        tcString: '',
        tcfVersion: '',
        tcStatus: '',
        data: acc
      };
      return mergePermissionModels(tempModel, val).data
    });

    Logger.debug(this.moduleName, 'syncModels', 'local model after sync:', JSON.stringify(this.permissionModel, null, 2));
    await this.permissionClient.setPermissions(this.permissionModel);
    tracker.track(CONFIG_CORE_SOFT_NAME, {section: 'permission.sync.localremote'});

    const newPermissions = getPermissionsWithoutTimestamps(this.permissionModel.data);
    /* istanbul ignore next else */
    if (newPermissions.length > 0) {
      await this.storePermissions(newPermissions);
    }
  }

  /**
   * add data to Sentry events
   */
  private setSentryTags(tagMap: { [key: string]: string }): void {
    for (const key in tagMap) {
      if (Object.prototype.hasOwnProperty.call(tagMap, key) && typeof tagMap[key] === 'string') {
        setTag(key, tagMap[key]);
      }
    }
  }

  /**
   * @function destroy
   * @description Function to exit correctly
   * @return {void}
   */
  public destroy(): void {
    if (this.parentConnection) {
      this.parentConnection.destroy();
      this.parentConnection = null;
    }

    /* istanbul ignore else */
    if (this.aditionService) {
      this.aditionService.destroy();
      this.aditionService = null;
    }

    this.permissionClient = null;
    this.permissionModel = null;
    this.clientConfig = null;
  }

}
